import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import TableTopControls from "common/TableTopControls";

import TarifasApi from "api/tarifasApi";
import LimitesTarifaModal from "./limitesTarifa";
import EditTarifaModal from "./editTarifaModal";

const TarifasTable = () => {
  const [tarifas, setTarifas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLimitesTarifaOpen, setModalLimitesTarifaOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTarifa, setSelectedTarifa] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const fetchTarifas = async () => {
    try {
      const response = await TarifasApi.fetchTarifas();
      setTarifas(response.data);
    } catch (error) {
      console.error("Error al obtener la tarifa:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTarifas();
  }, []);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let tarifa = tarifas.find((a) => a.id === id);
    setSelectedTarifa(tarifa);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleLimitesTarifa = (tarifa) => {
    setSelectedTarifa(tarifa);
    setModalLimitesTarifaOpen(true);
    handleMenuClose();
  };

  const handleEdit = (tarifa) => {
    setSelectedTarifa(tarifa);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro(a) de eliminar esta tarifa?")) {
      try {
        await TarifasApi.deleteTarifa(id);
        fetchTarifas();
      } catch (error) {
        console.error("Error al eliminar la tarifa:", error);
      }
    }
    handleMenuClose();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const filteredTarifas = tarifas.filter((tarifa) => {
    const matchesSearch =
      tarifa.ciudad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tarifa.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tarifa.direccion?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const paginatedTarifas = filteredTarifas.slice(page * pageSize, (page + 1) * pageSize);

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "sede",
      headerName: "Sede",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.sede_detalle.nombre}
        </SuiTypography>
      ),
    },
    {
      field: "tipo_proceso",
      headerName: "Tipo Proceso",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.tipo_proceso_detalle.abreviatura}
        </SuiTypography>
      ),
    },
    {
      field: "nombre",
      headerName: "Nombre",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombre}
        </SuiTypography>
      ),
    },
    {
      field: "salario_minimo",
      headerName: "Salario Mínimo",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.salario_minimo ? formatCurrency(params.row.salario_minimo) : "$ 0"}
        </SuiTypography>
      ),
    },
  ];

  return (
    <SuiBox>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Administración de Tarifas de las Sedes</strong>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox display="flex" alignItems="center" gap={2}>
            <Tooltip title="Buscar por Ciudad, Nombre y Dirección" arrow>
              <TextField
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Tooltip>
            <TableTopControls
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalRows={filteredTarifas.length}
            />
            <SuiButton
              variant="gradient"
              buttonColor="dark"
              size="small"
              onClick={() => {
                setSelectedTarifa(null);
                setEditModalOpen(true);
              }}
            >
              Nueva Tarifa
            </SuiButton>
          </SuiBox>
        </SuiBox>

        <DataGrid
          rows={paginatedTarifas}
          columns={columns}
          disableSelectionOnClick
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[100],
              },
              minHeight: "48px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "48px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleLimitesTarifa(tarifas.find((a) => a.id === selectedId))}>
          <AddCircleOutlineIcon title="Detalle Tarifa" fontSize="small" sx={{ mr: 1 }} /> Detalle
          Tarifa
        </MenuItem>
        <MenuItem onClick={() => handleEdit(tarifas.find((a) => a.id === selectedId))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedId)} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      <LimitesTarifaModal
        open={modalLimitesTarifaOpen}
        onClose={() => setModalLimitesTarifaOpen(false)}
        tarifa={selectedTarifa}
        onSuccess={() => {
          fetchTarifas();
          setModalLimitesTarifaOpen(false);
        }}
      />
      <EditTarifaModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        tarifa={selectedTarifa}
        onSuccess={() => {
          fetchTarifas();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default TarifasTable;
